// Single source of truth for status → label + color tone across the app.
// Every screen maps domain enums to a `Tone`; Badge/Dot translate tone → classes.
import type {
  InventoryStatus, Severity, RecommendationStatus, AlertLevel, OrderStatus, EventStatus, EventType,
} from '@shared/types';

export type Tone = 'danger' | 'warning' | 'success' | 'info' | 'violet' | 'primary' | 'neutral';

export interface Descriptor {
  label: string;
  tone: Tone;
}

export const TONE_BADGE: Record<Tone, string> = {
  danger: 'bg-danger-soft text-danger-text border-danger/30',
  warning: 'bg-warning-soft text-warning-text border-warning/30',
  success: 'bg-success-soft text-success-text border-success/30',
  info: 'bg-info-soft text-info-text border-info/30',
  violet: 'bg-violet-soft text-violet-text border-violet/30',
  primary: 'bg-primary-soft text-primary-text border-primary/30',
  neutral: 'bg-hover text-ink-muted border-hairline',
};

export const TONE_DOT: Record<Tone, string> = {
  danger: 'bg-danger',
  warning: 'bg-warning',
  success: 'bg-success',
  info: 'bg-info',
  violet: 'bg-violet',
  primary: 'bg-primary',
  neutral: 'bg-ink-faint',
};

export const TONE_TEXT: Record<Tone, string> = {
  danger: 'text-danger-text',
  warning: 'text-warning-text',
  success: 'text-success-text',
  info: 'text-info-text',
  violet: 'text-violet-text',
  primary: 'text-primary-text',
  neutral: 'text-ink-muted',
};

export const TONE_BAR: Record<Tone, string> = {
  danger: 'bg-danger',
  warning: 'bg-warning',
  success: 'bg-success',
  info: 'bg-info',
  violet: 'bg-violet',
  primary: 'bg-primary',
  neutral: 'bg-ink-faint',
};

export const inventoryStatus: Record<InventoryStatus, Descriptor> = {
  critico: { label: 'CRÍTICO', tone: 'danger' },
  alerta: { label: 'ALERTA', tone: 'warning' },
  saludable: { label: 'SALUDABLE', tone: 'success' },
  sobrestock: { label: 'SOBRESTOCK', tone: 'violet' },
  sin_datos: { label: 'SIN DATOS', tone: 'neutral' },
};

export const severity: Record<Severity, Descriptor> = {
  critico: { label: 'CRÍTICO', tone: 'danger' },
  alto: { label: 'ALTO', tone: 'warning' },
  medio: { label: 'MEDIO', tone: 'info' },
};

export const recommendationStatus: Record<RecommendationStatus, Descriptor> = {
  pendiente: { label: 'Pendiente', tone: 'neutral' },
  aprobado: { label: 'Aprobado', tone: 'success' },
  ajustado: { label: 'Ajustado', tone: 'info' },
};

export const alertLevel: Record<AlertLevel, Descriptor> = {
  critico: { label: 'CRÍTICO', tone: 'danger' },
  alerta: { label: 'ALERTA', tone: 'warning' },
  info: { label: 'INFO', tone: 'info' },
};

export const orderStatus: Record<OrderStatus, Descriptor> = {
  borrador: { label: 'Borrador', tone: 'neutral' },
  pend_aprobacion: { label: 'Pend. Aprobación', tone: 'warning' },
  en_transito: { label: 'En Tránsito', tone: 'info' },
  nacionalizacion: { label: 'Nacionalización', tone: 'violet' },
  recibida: { label: 'Recibida', tone: 'success' },
};

export const eventStatus: Record<EventStatus, Descriptor> = {
  activo: { label: 'Activo', tone: 'danger' },
  proximo: { label: 'Próximo', tone: 'info' },
  completado: { label: 'Completado', tone: 'neutral' },
};

export const eventType: Record<EventType, Descriptor> = {
  aumento_demanda: { label: 'Aumento Demanda', tone: 'success' },
  reduccion_demanda: { label: 'Reducción Demanda', tone: 'info' },
  demora_suministro: { label: 'Demora Suministro', tone: 'warning' },
  disrupcion_suministro: { label: 'Disrupción Suministro', tone: 'danger' },
};

/** Green ≤8% · Amber ≤12% · Red >12% (MAPE colour rule). */
export function mapeTone(mape: number): Tone {
  if (mape <= 8) return 'success';
  if (mape <= 12) return 'warning';
  return 'danger';
}
